// packages/core/src/badges/badgesRegistry.ts
// ─────────────────────────────────────────────────────────────────────────
// THE BADGE REGISTRY — single source of truth for which badges exist.
//
// Product-locked V1 set: 28 badges across 6 categories.
// V1.1 set: 6 badges DEFINED here but gated by release === 'v1.1' so they are
// neither shown nor awarded until promoted. Promotion = change one field.
//
// Badge IDs are PERMANENT. Do not rename ids or change their meaning. You may
// freely edit name / description / unlockCondition / icon / nextAction.
//
// Copy rules (from the tone guide): adult, direct, past-tense, earned. No
// "Epic achievement!", no "Crushed it!", no exclamation spam.
// ─────────────────────────────────────────────────────────────────────────

import type { BadgeCategory, BadgeDef, BadgeRelease } from './BadgeTypes';
import { BADGE_CATEGORIES, BADGE_CATEGORY_META } from './BadgeTypes';
import { DISCOVERY_QUIZZES } from '../quizzes';

export const BADGES: readonly BadgeDef[] = [
	// ── Getting Started (4) ──────────────────────────────────────────────
	{
		id: 'first_step',
		category: 'getting-started',
		name: 'First Step',
		description: 'Completed your first lesson.',
		unlockCondition: 'Complete 1 lesson.',
		icon: 'step',
		release: 'v1',
		awardMode: 'retroactive',
		nextAction: 'Pass the quiz to lock the lesson in.',
	},
	{
		id: 'checked_yourself',
		category: 'getting-started',
		name: 'Checked Yourself',
		description: 'Passed your first quiz.',
		unlockCondition: 'Pass 1 quiz.',
		icon: 'check',
		release: 'v1',
		awardMode: 'retroactive',
		nextAction: 'Master the flashcards so the lesson sticks.',
	},
	{
		id: 'made_it_real',
		category: 'getting-started',
		name: 'Made It Real',
		description: 'Used your first lesson action.',
		unlockCondition: 'Use 1 Apply action.',
		icon: 'spark',
		release: 'v1',
		awardMode: 'forward-only',
		nextAction: 'Run a what-if to see what a choice really costs.',
	},
	{
		id: 'first_session',
		category: 'getting-started',
		name: 'First Session',
		description: 'Completed your first real learning session.',
		unlockCondition: 'Complete any 2 learning actions in one day.',
		icon: 'door',
		release: 'v1',
		awardMode: 'retroactive',
	},

	// ── Run the Numbers (8) — the product moat ───────────────────────────
	{
		id: 'ran_the_numbers',
		category: 'run-the-numbers',
		name: 'Ran the Numbers',
		description: 'Tested a money choice using your own numbers.',
		unlockCondition: 'Run your first personalized scenario.',
		icon: 'calculator',
		release: 'v1',
		awardMode: 'forward-only',
		nextAction: 'Try a what-if scenario in Simulate.',
		celebration: 'major',
	},
	{
		id: 'found_your_number',
		category: 'run-the-numbers',
		name: 'Found Your Number',
		description: 'Calculated your FI number.',
		unlockCondition: 'Calculate your FI number.',
		icon: 'target',
		release: 'v1',
		awardMode: 'retroactive',
		nextAction: 'See how a choice moves your timeline in Impact Lab.',
	},
	{
		id: 'tested_a_choice',
		category: 'run-the-numbers',
		name: 'Tested a Choice',
		description: 'Ran your first what-if scenario.',
		unlockCondition: 'Run 1 what-if in Simulate.',
		icon: 'graph',
		release: 'v1',
		awardMode: 'forward-only',
	},
	{
		id: 'saw_the_hidden_cost',
		category: 'run-the-numbers',
		name: 'Saw the Hidden Cost',
		description: 'Used Impact Lab to see a choice over time.',
		unlockCondition: 'Run 1 Impact Lab scenario.',
		icon: 'compass',
		release: 'v1',
		awardMode: 'forward-only',
	},
	{
		id: 'intuition_tested',
		category: 'run-the-numbers',
		name: 'Intuition Tested',
		description: 'Completed your first Discovery quiz.',
		unlockCondition: 'Complete 1 Discovery quiz.',
		icon: 'compass',
		release: 'v1',
		awardMode: 'retroactive',
	},
	{
		id: 'intuition_explorer',
		category: 'run-the-numbers',
		name: 'Intuition Explorer',
		description: 'Completed every Discovery quiz.',
		unlockCondition: 'Complete all the Discovery quizzes.',
		icon: 'globe',
		release: 'v1',
		awardMode: 'retroactive',
		progressTarget: DISCOVERY_QUIZZES.length,
		progressUnit: 'quizzes',
	},
	{
		id: 'market_time_traveler',
		category: 'run-the-numbers',
		name: 'Market Time Traveler',
		description: 'Rode a real stretch of market history to the end.',
		unlockCondition: 'Finish 1 Market Replay scenario.',
		icon: 'graph',
		release: 'v1',
		awardMode: 'retroactive',
	},
	{
		id: 'freedom_plan_started',
		category: 'run-the-numbers',
		name: 'Freedom Plan Started',
		description: 'Created your first freedom projection.',
		unlockCondition: 'Generate your first freedom projection.',
		icon: 'path',
		release: 'v1',
		awardMode: 'forward-only',
		celebration: 'major',
	},

	// ── Mastery (7) ──────────────────────────────────────────────────────
	{
		id: 'locked_in_memory',
		category: 'mastery',
		name: 'Locked In Memory',
		description: 'Got the Memory Anchors solid for a lesson.',
		unlockCondition: 'Reach the Memory Anchor checkpoint for 1 lesson.',
		icon: 'lock',
		release: 'v1',
		awardMode: 'retroactive',
	},
	{
		id: 'first_try',
		category: 'mastery',
		name: 'First Try',
		description: 'Passed a quiz on your first attempt.',
		unlockCondition: 'Pass 1 quiz on the first attempt.',
		icon: 'check',
		release: 'v1',
		awardMode: 'retroactive',
	},
	{
		id: 'perfect_score',
		category: 'mastery',
		name: 'Perfect Score',
		description: 'Scored 100% on a quiz.',
		unlockCondition: 'Score 100% on any quiz.',
		icon: 'medal',
		release: 'v1',
		awardMode: 'retroactive',
	},
	{
		id: 'passed_the_test',
		category: 'mastery',
		name: 'Passed the Test',
		description: 'Passed your first module final test.',
		unlockCondition: 'Pass 1 final test.',
		icon: 'medal',
		release: 'v1',
		awardMode: 'retroactive',
	},
	{
		id: 'fully_trained',
		category: 'mastery',
		name: 'Fully Trained',
		description: 'Fully mastered your first lesson.',
		unlockCondition:
			'Read, pass the quiz, and get the Memory Anchors solid for 1 lesson.',
		icon: 'brain',
		release: 'v1',
		awardMode: 'retroactive',
	},
	{
		id: 'module_complete',
		category: 'mastery',
		name: 'Module Complete',
		description: 'Mastered your first module.',
		unlockCondition: 'Master 1 module.',
		icon: 'book',
		release: 'v1',
		awardMode: 'retroactive',
		celebration: 'major',
	},
	{
		id: 'pattern_builder',
		category: 'mastery',
		name: 'Pattern Builder',
		description: 'Mastered three modules.',
		unlockCondition: 'Master 3 modules.',
		icon: 'brain',
		release: 'v1',
		awardMode: 'retroactive',
		progressTarget: 3,
		progressUnit: 'modules',
		celebration: 'major',
	},

	// ── Consistency (4) — aligned with gem streak milestones 3/7/30 ──────
	{
		id: 'showing_up',
		category: 'consistency',
		name: 'Showing Up',
		description: 'Built a 3-day learning streak.',
		unlockCondition: 'Reach a 3-day streak.',
		icon: 'flame',
		release: 'v1',
		awardMode: 'retroactive',
		progressTarget: 3,
		progressUnit: 'days',
	},
	{
		id: 'one_week_in',
		category: 'consistency',
		name: 'One Week In',
		description: 'Built a 7-day learning streak.',
		unlockCondition: 'Reach a 7-day streak.',
		icon: 'calendar',
		release: 'v1',
		awardMode: 'retroactive',
		progressTarget: 7,
		progressUnit: 'days',
	},
	{
		id: 'locked_in',
		category: 'consistency',
		name: 'Locked In',
		description: 'Built a 30-day learning streak.',
		unlockCondition: 'Reach a 30-day streak.',
		icon: 'chain',
		release: 'v1',
		awardMode: 'retroactive',
		progressTarget: 30,
		progressUnit: 'days',
	},
	{
		id: 'daily_reviewer',
		category: 'consistency',
		name: 'Daily Reviewer',
		description: 'Cleared Daily Review on ten different days.',
		unlockCondition: 'Clear your Daily Review on 10 days.',
		icon: 'calendar',
		release: 'v1',
		awardMode: 'retroactive',
		progressTarget: 10,
		progressUnit: 'days',
	},

	// ── Worldviews (3 in V1; debate badges held for V1.1) ────────────────
	{
		id: 'entered_a_worldview',
		category: 'worldviews',
		name: 'Entered a Worldview',
		description: 'Completed your first school-connected lesson.',
		unlockCondition: 'Complete 1 lesson tied to a school of thought.',
		icon: 'prism',
		release: 'v1',
		awardMode: 'retroactive',
	},
	{
		id: 'worldview_explorer',
		category: 'worldviews',
		name: 'Worldview Explorer',
		description:
			'Completed lessons from three different schools of thought.',
		unlockCondition: 'Complete lessons across 3 schools.',
		icon: 'globe',
		release: 'v1',
		awardMode: 'retroactive',
		progressTarget: 3,
		progressUnit: 'schools',
	},
	{
		id: 'exhibit_visitor',
		category: 'worldviews',
		name: 'Exhibit Visitor',
		description: 'Read a school exhibit end to end.',
		unlockCondition: 'Read 1 school exhibit to the end.',
		icon: 'prism',
		release: 'v1',
		awardMode: 'retroactive',
	},

	// ── Freedom Path (2 in V1; deeper milestones held for V1.1) ──────────
	{
		id: 'getting_trained',
		category: 'freedom-path',
		name: 'Getting Trained',
		description: 'Reached Level 5.',
		unlockCondition: 'Reach Level 5.',
		icon: 'flag',
		release: 'v1',
		awardMode: 'retroactive',
		progressTarget: 5,
		progressUnit: 'levels',
	},
	{
		id: 'foundation_built',
		category: 'freedom-path',
		name: 'Foundation Built',
		description: 'Completed the Foundation tier.',
		unlockCondition: 'Master every module in the Foundation tier.',
		icon: 'mountain',
		release: 'v1',
		awardMode: 'retroactive',
		celebration: 'major',
	},

	// ── V1.1 — DEFINED BUT HELD (release: 'v1.1') ────────────────────────
	// Promote to 'v1' once debate content + deeper tiers are mature.
	{
		id: 'tradeoff_thinker',
		category: 'worldviews',
		name: 'Tradeoff Thinker',
		description: 'Read your first school debate.',
		unlockCondition: 'Read 1 debate.',
		icon: 'conversation',
		release: 'v1.1',
		awardMode: 'forward-only',
	},
	{
		id: 'sees_more_than_one_side',
		category: 'worldviews',
		name: 'Sees More Than One Side',
		description: 'Explored both sides of a money debate.',
		unlockCondition: 'Engage with a debate and a lesson from each side.',
		icon: 'conversation',
		release: 'v1.1',
		awardMode: 'forward-only',
	},
	{
		id: 'seasoned',
		category: 'freedom-path',
		name: 'Seasoned',
		description: 'Reached Level 10.',
		unlockCondition: 'Reach Level 10.',
		icon: 'flag',
		release: 'v1.1',
		awardMode: 'retroactive',
		progressTarget: 10,
		progressUnit: 'levels',
	},
	{
		id: 'builder_built',
		category: 'freedom-path',
		name: 'Builder Built',
		description: 'Completed the Builder tier.',
		unlockCondition: 'Master every module in the Builder tier.',
		icon: 'mountain',
		release: 'v1.1',
		awardMode: 'retroactive',
		celebration: 'major',
	},
	{
		id: 'freedom_ready',
		category: 'freedom-path',
		name: 'Freedom Ready',
		description: 'Completed the Freedom tier.',
		unlockCondition: 'Master every module in the Freedom tier.',
		icon: 'mountain',
		release: 'v1.1',
		awardMode: 'retroactive',
		celebration: 'major',
	},
	{
		id: 'advanced_perspective',
		category: 'freedom-path',
		name: 'Advanced Perspective',
		description: 'Completed your first Advanced module.',
		unlockCondition: 'Master 1 Advanced-tier module.',
		icon: 'mountain',
		release: 'v1.1',
		awardMode: 'retroactive',
	},
] as const;

// ── Lookups & guards ─────────────────────────────────────────────────────

const BADGE_BY_ID: ReadonlyMap<string, BadgeDef> = new Map(
	BADGES.map((b) => [b.id, b]),
);

export function getBadge(id: string): BadgeDef | undefined {
	return BADGE_BY_ID.get(id);
}

export function isKnownBadge(id: string): boolean {
	return BADGE_BY_ID.has(id);
}

/** All badges for a release gate (defaults to the live V1 set). */
export function getBadgesForRelease(release: BadgeRelease = 'v1'): BadgeDef[] {
	// 'v1' shows only v1. 'v1.1' shows v1 + v1.1 (superset).
	return BADGES.filter((b) => (release === 'v1' ? b.release === 'v1' : true));
}

/** Badges in a category, filtered to the active release, registry order. */
export function getBadgesInCategory(
	category: BadgeCategory,
	release: BadgeRelease = 'v1',
): BadgeDef[] {
	return getBadgesForRelease(release).filter((b) => b.category === category);
}

/** Categories that contain at least one badge for the active release,
 *  in product-story order. */
export function getOrderedCategories(release: BadgeRelease = 'v1') {
	return [...BADGE_CATEGORIES]
		.filter((c) => getBadgesInCategory(c, release).length > 0)
		.sort(
			(a, b) =>
				BADGE_CATEGORY_META[a].order - BADGE_CATEGORY_META[b].order,
		)
		.map((c) => BADGE_CATEGORY_META[c]);
}

/** Total badge count for a release (used for "n / TOTAL earned"). */
export function getTotalBadgeCount(release: BadgeRelease = 'v1'): number {
	return getBadgesForRelease(release).length;
}
